import React, { useMemo } from "react";
import "./profitRanking.css";
import useProfitRanking from "../../hooks/useProfitRanking";

function ProfitRanking({ className }) {
  const { profitRanking, loadingProfitRanking, error, refetch } =
    useProfitRanking();

  const overallAverage = useMemo(() => {
    if (!profitRanking.length) return { value: "0%", isPositive: true };
    const sum = profitRanking.reduce((acc, asset) => {
      const profit = asset.profit || 0;
      return acc + profit;
    }, 0);
    const avg = sum / profitRanking.length;
    return {
      value: `${avg > 0 ? "+" : ""}${avg.toFixed(1)}%`,
      isPositive: avg >= 0,
    };
  }, [profitRanking]);

  const TrendIcon = ({ isUp }) => (
    <div
      className={`orion__profit-ranking-trend-icon ${isUp ? "orion__profit-ranking-trend-up" : "orion__profit-ranking-trend-down"}`}
    >
      {isUp ? "↗" : "↘"}
    </div>
  );

  if (loadingProfitRanking) {
    return (
      <div className={`orion__profit-ranking-container ${className || ""}`}>
        <div className="orion__profit-ranking-table">
          <div className="orion__profit-ranking-loading">
            <div className="orion__profit-ranking-loading-text">
              Carregando ranking...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`orion__profit-ranking-container ${className || ""}`}>
        <div className="orion__profit-ranking-table">
          <div className="orion__profit-ranking-error">
            <p className="orion__profit-ranking-error-text">{error}</p>
            <button
              onClick={refetch}
              className="orion__profit-ranking-error-button"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`orion__profit-ranking-container ${className || ""}`}>
      <div className="orion__profit-ranking-table">
        <div className="orion__profit-ranking-header">
          <h3 className="orion__profit-ranking-title">Ranking de Lucros</h3>
          <div className="orion__profit-ranking-live-indicator">
            <div className="orion__profit-ranking-live-dot"></div>
            <span>AO VIVO</span>
          </div>
        </div>

        <div className="orion__profit-ranking-average">
          <div className="orion__profit-ranking-average-info">
            <h4>Média Geral</h4>
            <p
              className={`orion__profit-ranking-average-value ${!overallAverage.isPositive ? "negative" : ""}`}
            >
              {overallAverage.value}
            </p>
          </div>
          <TrendIcon isUp={overallAverage.isPositive} />
        </div>

        <div className="orion__profit-ranking-list">
          {profitRanking.slice(0, 5).map((asset, index) => {
            const profit = asset.profit || 0;
            const isPositive = profit >= 0;

            const maxProfit =
              profitRanking.length > 0
                ? Math.max(...profitRanking.map((a) => Math.abs(a.profit || 0)))
                : 100;
            const normalizedWidth = (Math.abs(profit) / maxProfit) * 85;
            const progressWidth = Math.min(Math.max(normalizedWidth, 5), 85);

            const profitString = `${profit > 0 ? "+" : ""}${profit.toFixed(1)}%`;

            return (
              <div
                key={asset.pair || index}
                className="orion__profit-ranking-asset"
              >
                <div className="orion__profit-ranking-asset-header">
                  <div className="orion__profit-ranking-left-section">
                    <div className="orion__profit-ranking-position">
                      {asset.position || index + 1}
                    </div>
                    <div className="orion__profit-ranking-asset-info">
                      <div className="orion__profit-ranking-asset-name">
                        {asset.name || "N/A"}
                      </div>
                      <div className="orion__profit-ranking-pair-name">
                        {asset.pair || "N/A"}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`orion__profit-ranking-profit ${!isPositive ? "negative" : ""}`}
                  >
                    {profitString}
                  </div>
                </div>

                <div className="orion__profit-ranking-progress-container">
                  <div
                    className={`orion__profit-ranking-progress-bar ${
                      isPositive
                        ? "orion__profit-ranking-progress-positive"
                        : "orion__profit-ranking-progress-negative"
                    }`}
                    style={{ width: `${progressWidth}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ProfitRanking;
